import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createCommentVNode as _createCommentVNode, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = {
  class: "container-fluid",
  style: {"margin-top":"80px","padding-top":"20px"}
}
const _hoisted_2 = { class: "row" }
const _hoisted_3 = { class: "col-lg-6" }
const _hoisted_4 = { class: "text-muted mt-2" }

import { computed, onMounted, watch, shallowReactive, nextTick, ref, type Ref } from 'vue'
    import { useRoute } from 'vue-router'

    import PortfolioNavigation from '@common/Components/PortfolioNavigation.vue'
    import { MonthYear } from '@common/types'

    import { useProposedProjectsPageQueryV1 } from '@Quest/Portfolios/queries-vue-composables'
    import { useSaveViewProposedProjectsPageCommandV1 } from '@Quest/Portfolios/commands-vue-composables'

    import { useGridProjectByIdQueryV1 } from '@Quest/Projects/queries-vue-composables'

    import
        {
            useSetLifecycleInProgressCommandV1,
            useSetLifecycleProposalCommandV1,
            useSetLifecycleQualifiedCommandV1,
            useSetLifecycleRejectedCommandV1,
            useSetLifecycleScheduledCommandV1
        } from '@Quest/Projects/commands-vue-composables'

    import { ProposedProjectsPageReadModel } from '@Quest/Portfolios/readmodels'
    import { GridProjectReadModel } from '@Quest/Projects/readmodels'

    import { ProjectsGridViewModel } from '@Quest/Projects/Components/ProjectsGrid'
    import ProjectsGrid from '@Quest/Projects/Components/ProjectsGrid.vue'

    import { ProjectProposalLifecycleSetterViewModel } from '@Quest/Projects/Components/ProjectProposalLifecycleSetter'
    import ProjectProposalLifecycleSetter from '@Quest/Projects/Components/ProjectProposalLifecycleSetter.vue'

    import { ProjectQualifiedLifecycleSetterViewModel } from '@Quest/Projects/Components/ProjectQualifiedLifecycleSetter'
    import ProjectQualifiedLifecycleSetter from '@Quest/Projects/Components/ProjectQualifiedLifecycleSetter.vue'

    import { ProjectRejectedLifecycleSetterViewModel } from '@Quest/Projects/Components/ProjectRejectedLifecycleSetter'
    import ProjectRejectedLifecycleSetter from '@Quest/Projects/Components/ProjectRejectedLifecycleSetter.vue'

    import { ProjectScheduledLifecycleSetterViewModel } from '@Quest/Projects/Components/ProjectScheduledLifecycleSetter'
    import ProjectScheduledLifecycleSetter from '@Quest/Projects/Components/ProjectScheduledLifecycleSetter.vue'

    import { ProjectInProgressLifecycleSetterViewModel } from '@Quest/Projects/Components/ProjectInProgressLifecycleSetter'
    import ProjectInProgressLifecycleSetter from '@Quest/Projects/Components/ProjectInProgressLifecycleSetter.vue'



    // ======================
    // Route
    // ======================

    
export default /*@__PURE__*/_defineComponent({
  __name: 'ProposedProjectsPage',
  setup(__props) {

    const route = useRoute()

    const workspaceId = computed(() => Number(route.params.workspaceId ?? 0))
    const tchProjectId = computed(() => Number(route.params.tchProjectId ?? 0))



    // ======================
    // Query
    // ======================

    const {
        runProposedProjectsPageQueryV1,
        modelProposedProjectsPageQueryV1
    } = useProposedProjectsPageQueryV1(workspaceId, tchProjectId)

    const rm = modelProposedProjectsPageQueryV1 as Ref<ProposedProjectsPageReadModel | null>

    const portfolioId = computed(() => rm.value?.PortfolioId ?? '')
    const portfolioName = computed(() => rm.value?.PortfolioName ?? null)



    // ======================
    // Local Success Caches
    // ======================

    const lastProposalId = ref<string | null>(null)
    const lastQualifiedId = ref<string | null>(null)
    const lastRejectedId = ref<string | null>(null)
    const lastScheduledId = ref<string | null>(null)
    const lastInProgressId = ref<string | null>(null)



    // ======================
    // Grid project by id query
    // ======================

    const {
        runGridProjectByIdQueryV1,
        modelGridProjectByIdQueryV1
    } = useGridProjectByIdQueryV1(workspaceId, portfolioId)



    // ======================
    // Commands
    // ======================

    const {
        invokeSaveViewProposedProjectsPageCommandV1,
        succeedSaveViewProposedProjectsPageCommandV1
    } = useSaveViewProposedProjectsPageCommandV1(workspaceId, portfolioId)

    const {
        invokeSetLifecycleProposalCommandV1,
        succeedSetLifecycleProposalCommandV1
    } = useSetLifecycleProposalCommandV1(workspaceId, portfolioId, lastProposalId)

    const {
        invokeSetLifecycleQualifiedCommandV1,
        succeedSetLifecycleQualifiedCommandV1
    } = useSetLifecycleQualifiedCommandV1(workspaceId, portfolioId, lastQualifiedId)

    const {
        invokeSetLifecycleRejectedCommandV1,
        succeedSetLifecycleRejectedCommandV1
    } = useSetLifecycleRejectedCommandV1(workspaceId, portfolioId, lastRejectedId)

    const {
        invokeSetLifecycleScheduledCommandV1,
        succeedSetLifecycleScheduledCommandV1
    } = useSetLifecycleScheduledCommandV1(workspaceId, portfolioId, lastScheduledId)

    const {
        invokeSetLifecycleInProgressCommandV1,
        succeedSetLifecycleInProgressCommandV1
    } = useSetLifecycleInProgressCommandV1(workspaceId, portfolioId, lastInProgressId)



    // ======================
    // ViewModels
    // ======================

    const grid = shallowReactive(new ProjectsGridViewModel())

    const projectQualifiedLifecycleSetter = shallowReactive(new ProjectQualifiedLifecycleSetterViewModel())
    const projectRejectedLifecycleSetter = shallowReactive(new ProjectRejectedLifecycleSetterViewModel())
    const projectProposalLifecycleSetter = shallowReactive(new ProjectProposalLifecycleSetterViewModel())
    const projectScheduledLifecycleSetter = shallowReactive(new ProjectScheduledLifecycleSetterViewModel())
    const projectInProgressLifecycleSetter = shallowReactive(new ProjectInProgressLifecycleSetterViewModel())



    // ======================
    // Handlers
    // ======================

    function handleShowGridColumnsEditor()
    {
        grid.showGridColumnsEditor()
    }

    function handleSaveViewSettings()
    {
        invokeSaveViewProposedProjectsPageCommandV1({
            Grid: grid.getJson()
        })
    }

    function handleClearGridFilters()
    {
        grid.clearFilters()
    }

    function handleExportGrid()
    {
        grid.exportGrid()
    }

    function handleProposed(projectId: string)
    {
        lastProposalId.value = projectId
        invokeSetLifecycleProposalCommandV1()
    }

    function handleQualified(projectId: string)
    {
        lastQualifiedId.value = projectId
        invokeSetLifecycleQualifiedCommandV1()
    }

    function handleRejected(projectId: string)
    {
        lastRejectedId.value = projectId
        invokeSetLifecycleRejectedCommandV1()
    }

    function handleScheduled(projectId: string, startDate: MonthYear)
    {
        lastScheduledId.value = projectId
        invokeSetLifecycleScheduledCommandV1({ StartDate: startDate })
    }

    function handleStarted(projectId: string, startDate: MonthYear)
    {
        lastInProgressId.value = projectId
        invokeSetLifecycleInProgressCommandV1({ StartDate: startDate })
    }



    // ======================
    // Query Watchers
    // ======================

    watch(rm, (current) =>
    {
        if (!current) return

        nextTick(() =>
        {
            grid.renderGrid(
                current.Grid.IdField,
                current.Grid.Fields,
                current.Grid.DefaultFieldIds,
                current.Grid.Options
            )

            grid.setProjects(current.Projects.map(p => p.Properties))
        })
    })

    watch(modelGridProjectByIdQueryV1 as Ref<GridProjectReadModel | null>, (current) =>
    {
        if (!current || !rm.value) return

        rm.value.Projects = rm.value.Projects.filter(p => p.Id !== current.Id)
        rm.value.Projects.push(current)

        grid.update(current.Properties)
    })



    // ======================
    // Command Watchers
    // ======================

    function refreshProject(projectId: string)
    {
        // NOTE: keep payload shape consistent with your generated composable
        runGridProjectByIdQueryV1({ ProjectId: projectId })
    }

    watch(succeedSaveViewProposedProjectsPageCommandV1, (c, p) =>
    {
        if (c && !p) alert('Saved')
    })

    watch(succeedSetLifecycleProposalCommandV1, (c, p) =>
    {
        if (c && !p && lastProposalId.value)
        {
            refreshProject(lastProposalId.value)
            projectProposalLifecycleSetter.close()
            lastProposalId.value = null
        }
    })

    watch(succeedSetLifecycleQualifiedCommandV1, (c, p) =>
    {
        if (c && !p && lastQualifiedId.value)
        {
            refreshProject(lastQualifiedId.value)
            projectQualifiedLifecycleSetter.close()
            lastQualifiedId.value = null
        }
    })

    watch(succeedSetLifecycleRejectedCommandV1, (c, p) =>
    {
        if (c && !p && lastRejectedId.value)
        {
            refreshProject(lastRejectedId.value)
            projectRejectedLifecycleSetter.close()
            lastRejectedId.value = null
        }
    })

    watch(succeedSetLifecycleScheduledCommandV1, (c, p) =>
    {
        if (c && !p && lastScheduledId.value && rm.value)
        {
            const projectId = lastScheduledId.value

            rm.value.Projects = rm.value.Projects.filter(a => a.Id !== projectId)
            grid.removeRowById(projectId)

            projectScheduledLifecycleSetter.close()
            lastScheduledId.value = null
        }
    })

    watch(succeedSetLifecycleInProgressCommandV1, (c, p) =>
    {
        if (c && !p && lastInProgressId.value && rm.value)
        {
            const projectId = lastInProgressId.value

            rm.value.Projects = rm.value.Projects.filter(a => a.Id !== projectId)
            grid.removeRowById(projectId)

            projectInProgressLifecycleSetter.close()
            lastInProgressId.value = null
        }
    })



    // ======================
    // Lifecycle
    // ======================

    onMounted(() =>
    {
        document.title = "Portfolio Proposed | TransparentChoice"
        runProposedProjectsPageQueryV1()
    })

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", null, [
    _createVNode(ProjectProposalLifecycleSetter, {
      viewModel: _unref(projectProposalLifecycleSetter),
      onConfirm: handleProposed
    }, null, 8 /* PROPS */, ["viewModel"]),
    _createVNode(ProjectQualifiedLifecycleSetter, {
      viewModel: _unref(projectQualifiedLifecycleSetter),
      onConfirm: handleQualified
    }, null, 8 /* PROPS */, ["viewModel"]),
    _createVNode(ProjectRejectedLifecycleSetter, {
      viewModel: _unref(projectRejectedLifecycleSetter),
      onConfirm: handleRejected
    }, null, 8 /* PROPS */, ["viewModel"]),
    _createVNode(ProjectScheduledLifecycleSetter, {
      viewModel: _unref(projectScheduledLifecycleSetter),
      onConfirm: handleScheduled
    }, null, 8 /* PROPS */, ["viewModel"]),
    _createVNode(ProjectInProgressLifecycleSetter, {
      viewModel: _unref(projectInProgressLifecycleSetter),
      onConfirm: handleStarted
    }, null, 8 /* PROPS */, ["viewModel"]),
    _createVNode(PortfolioNavigation, {
      workspaceId: workspaceId.value,
      projectId: tchProjectId.value
    }, null, 8 /* PROPS */, ["workspaceId", "projectId"]),
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("div", _hoisted_3, [
          _createElementVNode("h6", _hoisted_4, _toDisplayString(portfolioName.value), 1 /* TEXT */),
          _cache[0] || (_cache[0] = _createElementVNode("h1", { style: {"font-size":"2em","font-weight":"300","color":"#33475b","margin":"0"} }, "Project proposals", -1 /* CACHED */))
        ]),
        _createElementVNode("div", { class: "col-lg-6 text-right align-self-end" }, [
          _createElementVNode("button", {
            role: "button",
            class: "btn btn-outline-secondary",
            style: {"padding":"0.7em 2em"},
            onClick: handleSaveViewSettings
          }, "Save")
        ])
      ]),
      _cache[4] || (_cache[4] = _createElementVNode("hr", { style: {"border-color":"#bbb","margin":"20px 0 60px"} }, null, -1 /* CACHED */)),
      _createElementVNode("div", { class: "toolbar" }, [
        _cache[3] || (_cache[3] = _createElementVNode("div", null, [
          _createCommentVNode("<button type=\"button\" class=\"btn btn-outline-secondary\">Action ({{selectedIds.length}})</button>")
        ], -1 /* CACHED */)),
        _createElementVNode("div", null, [
          _createElementVNode("button", {
            type: "button",
            class: "btn btn-outline-secondary mr-1",
            onClick: handleShowGridColumnsEditor
          }, "Edit columns"),
          _createElementVNode("div", { class: "btn-group" }, [
            _cache[1] || (_cache[1] = _createElementVNode("button", {
              type: "button",
              class: "btn btn-outline-secondary dropdown-toggle mr-1",
              "data-toggle": "dropdown"
            }, " Actions ", -1 /* CACHED */)),
            _createElementVNode("div", { class: "dropdown-menu dropdown-menu-right" }, [
              _createElementVNode("a", {
                class: "dropdown-item",
                onClick: handleClearGridFilters
              }, "Clear Filters")
            ])
          ]),
          _createElementVNode("button", {
            type: "button",
            class: "btn btn-outline-secondary",
            onClick: handleExportGrid,
            title: "Download to spreadsheet"
          }, [...(_cache[2] || (_cache[2] = [
            _createElementVNode("i", { class: "fa fa-download fa-lg" }, null, -1 /* CACHED */)
          ]))])
        ])
      ]),
      _createVNode(ProjectsGrid, { viewModel: _unref(grid) }, null, 8 /* PROPS */, ["viewModel"])
    ])
  ]))
}
}

})